import React, { useEffect, useRef, useState } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import { frameworksApi, formatFrameworkError } from '../../api/frameworks';
import { readBuilderSession, subscribeBuilderSession, writeBuilderSession } from './builderSession';
import { storiesApi } from '../../api/stories';
import { useApp } from '../../context/AppContext';
import { openBookDiscovery } from '../../utils/bookDiscoverySession';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import {
  FRAMEWORK_TALK_DISCOVERY,
  FRAMEWORK_TALK_DISCOVERY_NO_BOOK,
  FRAMEWORK_TALK_OPENING,
  FRAMEWORK_TALK_RESEARCH,
  FRAMEWORK_TALK_RESEARCH_NEAREST,
  FRAMEWORK_TALK_SOURCES,
  FRAMEWORK_TALK_UNREACHABLE,
} from '../../utils/writerCopy';
import ThinGenreMarker from './ThinGenreMarker';

const PART_PURPOSE = 'What happens in this sequence.';

const emptyPart = (name = '') => ({
  name,
  act: 1,
  purpose: PART_PURPOSE,
  cite_kind: 'general',
  cite_titles: [],
});

/**
 * Build a Framework: chat on one side, an editable parts list on the other.
 * Write my own is the same list without the chat. No form step and no
 * monospace dump — parts show up as the chat proposes them.
 */
export default function FrameworkCreateFlow({
  onCancel,
  onSave = null,
  saving = false,
  saveError = null,
  manual = false,
  storyId = null,
}) {
  const app = useApp() || {};
  const bookId = storyId || app.currentStory?.id || null;
  const savedSession = manual ? null : readBuilderSession(bookId);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState(
    savedSession?.messages?.length
      ? savedSession.messages
      : (manual ? [] : [{ role: 'assistant', content: FRAMEWORK_TALK_OPENING }]),
  );
  const [draft, setDraft] = useState(savedSession?.draft || null);
  const [name, setName] = useState(savedSession?.name || '');
  const [parts, setParts] = useState(savedSession?.parts || []);
  const [editing, setEditing] = useState(null);
  const [research, setResearch] = useState(savedSession?.research || null);
  const [offerDiscovery, setOfferDiscovery] = useState(Boolean(savedSession?.offerDiscovery));
  const [busy, setBusy] = useState(Boolean(savedSession?.pending));
  const [savingNow, setSavingNow] = useState(false);
  const [error, setError] = useState(savedSession?.error || null);
  const partsDirty = useRef(Boolean(savedSession?.partsDirty));
  const nameDirty = useRef(Boolean(savedSession?.nameDirty));
  const { ask, dialog } = usePaidConfirm();
  const inRouter = useInRouterContext();
  const messagesRef = useRef(messages);
  const partsRef = useRef(parts);
  const nameRef = useRef(name);
  const localRevision = useRef(savedSession?.revision || 0);
  messagesRef.current = messages;
  partsRef.current = parts;
  nameRef.current = name;

  const publish = (patch) => {
    const session = writeBuilderSession(bookId, patch);
    localRevision.current = session.revision;
    return session;
  };

  const applySession = (session) => {
    if (!session || session.cleared) return;
    setMessages(session.messages || []);
    setDraft(session.draft || null);
    setParts(session.parts || []);
    setName(session.name || '');
    setResearch(session.research || null);
    setOfferDiscovery(Boolean(session.offerDiscovery));
    setBusy(Boolean(session.pending));
    setError(session.error || null);
    partsDirty.current = Boolean(session.partsDirty);
    nameDirty.current = Boolean(session.nameDirty);
  };

  useEffect(() => {
    if (manual) return undefined;
    return subscribeBuilderSession((session) => {
      if ((session.storyId || 'none') !== (bookId || 'none')) return;
      if (session.revision === localRevision.current) return;
      localRevision.current = session.revision;
      applySession(session);
    });
  }, [bookId, manual]);

  const applyTurn = (result, baseMessages) => {
    const replyMessages = [...(baseMessages || messagesRef.current)];
    if (result.assistant_message) replyMessages.push({ role: 'assistant', content: result.assistant_message });
    if (result.decline_line) replyMessages.push({ role: 'assistant', content: result.decline_line });
    const next = result.draft || null;
    const nextParts = next?.beats && !partsDirty.current
      ? next.beats.map((beat) => ({ ...emptyPart(), ...beat, name: beat.name || '' }))
      : partsRef.current;
    const nextName = next?.framework_name && !nameDirty.current
      ? next.framework_name
      : nameRef.current;
    const keptClosed = Boolean(readBuilderSession(bookId)?.writerClosed);
    publish({
      messages: replyMessages,
      draft: next,
      parts: nextParts,
      name: nextName,
      research: result.research || null,
      offerDiscovery: Boolean(result.offer_discovery),
      pending: false,
      error: null,
      partsDirty: partsDirty.current,
      nameDirty: nameDirty.current,
      writerClosed: keptClosed,
    });
    setMessages(replyMessages);
    setDraft(next);
    setParts(nextParts);
    setName(nextName);
    setResearch(result.research || null);
    setOfferDiscovery(Boolean(result.offer_discovery));
    setError(null);
  };

  const localDraft = () => {
    const named = parts.filter((part) => String(part.name || '').trim());
    if (!draft && !named.length && !name.trim()) return null;
    return {
      ...(draft || {}),
      framework_name: name.trim() || draft?.framework_name || '',
      beats: (named.length ? named : (draft?.beats || [])).map((part) => ({
        ...emptyPart(),
        ...part,
        name: String(part.name || '').trim(),
        purpose: String(part.purpose || '').trim() || PART_PURPOSE,
      })),
    };
  };

  const sendTurn = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || busy) return;
    setError(null);
    const ok = await ask('framework_builder');
    if (!ok) return;
    const history = messagesRef.current;
    const outgoing = localDraft();
    const nextMessages = [...history, { role: 'user', content: trimmed }];
    messagesRef.current = nextMessages;
    setMessages(nextMessages);
    setInput('');
    setBusy(true);
    publish({
      messages: nextMessages,
      draft: outgoing,
      parts: partsRef.current,
      name: nameRef.current,
      research,
      offerDiscovery,
      pending: true,
      error: null,
      partsDirty: partsDirty.current,
      nameDirty: nameDirty.current,
      writerClosed: false,
    });
    try {
      const result = await frameworksApi.createTalk({
        message: trimmed,
        history,
        draft: outgoing,
        ...(bookId ? { story_id: bookId } : {}),
      });
      applyTurn(result, nextMessages);
    } catch (err) {
      const message = formatFrameworkError(err);
      const shown = err instanceof TypeError ? FRAMEWORK_TALK_UNREACHABLE : message;
      const keptClosed = Boolean(readBuilderSession(bookId)?.writerClosed);
      publish({ pending: false, error: shown, writerClosed: keptClosed });
      setError(shown);
    } finally {
      setBusy(false);
    }
  };

  const renamePart = (index, value) => {
    partsDirty.current = true;
    setParts((prev) => prev.map((part, i) => (i === index ? { ...part, name: value } : part)));
  };

  const addPart = () => {
    partsDirty.current = true;
    setEditing(parts.length);
    setParts((prev) => [...prev, emptyPart('')]);
  };

  const canSave = Boolean(name.trim()) && parts.some((part) => String(part.name || '').trim());

  const handleSave = async () => {
    if (!onSave || !canSave || busy || saving) return;
    const body = localDraft();
    if (!body) return;
    setSavingNow(true);
    setBusy(true);
    setError(null);
    try {
      if (!manual && draft) {
        const result = await frameworksApi.createBuild({ draft: body });
        const built = result?.framework || body;
        onSave({
          ...built,
          framework_name: body.framework_name,
          beats: body.beats,
        });
      } else {
        onSave(body);
      }
    } catch (err) {
      setError(formatFrameworkError(err));
    } finally {
      setSavingNow(false);
      setBusy(false);
    }
  };

  const savePending = Boolean(saving || savingNow);

  return (
    <div className={`fw-build${manual ? ' fw-build--manual' : ''}`}>
      {dialog}
      <div className="fw-build__bar">
        <h2 className="fw-build__title">{manual ? 'Write my own' : 'Build a Framework'}</h2>
        <button type="button" className="fw-btn fw-btn--ghost" onClick={onCancel} disabled={savePending}>
          Back to Frameworks
        </button>
        <span className="fw-build__spacer" />
        <button
          type="button"
          className="fw-btn fw-btn--primary"
          onClick={handleSave}
          disabled={!canSave || busy || savePending}
        >
          {savePending ? 'Saving…' : 'Save as my Framework'}
        </button>
      </div>
      <div className="fw-build__body">
        <section className="fw-build__parts" aria-label="Your Framework so far">
          <p className="fw-build__kicker">Your Framework so far</p>
          <label className="fw-field">
            <span className="fw-field__label">Framework name</span>
            <input
              className="fw-input"
              aria-label="Framework name"
              value={name}
              maxLength={100}
              onChange={(e) => {
                nameDirty.current = true;
                setName(e.target.value);
              }}
            />
          </label>
          <ol className="fw-parts">
            {parts.map((part, index) => (
              <li key={`part-${index}`} className="fw-part">
                <span className="fw-part__n">Sequence {index + 1}</span>
                {editing === index ? (
                  <input
                    className="fw-input"
                    aria-label={`Sequence ${index + 1} name`}
                    value={part.name}
                    autoFocus
                    onChange={(e) => renamePart(index, e.target.value)}
                    onBlur={() => setEditing(null)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setEditing(null);
                      }
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="fw-part__name"
                    onClick={() => setEditing(index)}
                  >
                    {part.name.trim() || 'Untitled sequence'}
                  </button>
                )}
              </li>
            ))}
          </ol>
          <button type="button" className="fw-part-add" onClick={addPart}>
            + Add a sequence
          </button>
          <p className="fw-muted">
            {manual
              ? 'Click a sequence name to rename it.'
              : 'Click a sequence name to rename it. Sequences appear here as you answer in chat.'}
          </p>
        </section>
        {!manual && (
          <section className="fw-build__chat" aria-label="Chat">
            <h3 className="fw-build__chat-title">Chat · Build</h3>
            <ThinGenreMarker show={Boolean(draft?.thin_genre)} />
            <ol className="fw-build__log" aria-live="polite">
              {messages.map((entry, index) => (
                <li key={`${entry.role}-${index}`} className={`fw-build__msg fw-build__msg--${entry.role}`}>
                  {entry.content}
                </li>
              ))}
            </ol>
            {research && (
              <details className="fw-create__research" data-testid="fw-talk-research">
                <summary>
                  {research.nearest
                    ? FRAMEWORK_TALK_RESEARCH_NEAREST(research.name)
                    : FRAMEWORK_TALK_RESEARCH(research.name)}
                </summary>
                <p className="fw-muted">{FRAMEWORK_TALK_SOURCES}</p>
                <ul>
                  {(research.sources || []).map((source) => (
                    <li key={source.title}>
                      {source.title}
                      {source.by ? ` (${source.by})` : ''}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {offerDiscovery && (
              inRouter
                ? <DiscoveryOffer />
                : <p className="fw-muted" data-testid="fw-talk-discovery">{FRAMEWORK_TALK_DISCOVERY_NO_BOOK}</p>
            )}
            {error && <p className="fw-error" role="alert">{error}</p>}
            {saveError && <p className="fw-error" role="alert">{saveError}</p>}
            <div className="fw-build__compose">
              <textarea
                className="fw-textarea"
                aria-label="Your reply"
                rows={3}
                placeholder="A genre, a book you love, or a feeling"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={busy}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendTurn(input);
                  }
                }}
              />
              <button
                type="button"
                className="fw-btn fw-btn--ghost"
                onClick={() => sendTurn(input)}
                disabled={busy || !input.trim()}
              >
                {busy ? 'Working…' : 'Send'}
              </button>
            </div>
          </section>
        )}
        {manual && (error || saveError) && (
          <p className="fw-error" role="alert">{error || saveError}</p>
        )}
      </div>
    </div>
  );
}

/** Browsing with no story yet: the Discovery chat is where an idea gets talked out. */
function DiscoveryOffer() {
  const navigate = useNavigate();
  const app = useApp() || {};
  const { currentStory, currentProject, stories, setCurrentStory } = app;
  if (!currentStory && !currentProject) {
    return <p className="fw-muted" data-testid="fw-talk-discovery">{FRAMEWORK_TALK_DISCOVERY_NO_BOOK}</p>;
  }
  return (
    <div className="fw-dialog-actions" data-testid="fw-talk-discovery">
      <button
        type="button"
        className="fw-btn fw-btn--ghost"
        onClick={() => {
          openBookDiscovery({
            currentStory,
            currentProject,
            stories,
            navigate,
            setCurrentStory,
            ensureDiscoverySession: storiesApi.ensureDiscoverySession,
          }).catch(() => {});
        }}
      >
        {FRAMEWORK_TALK_DISCOVERY}
      </button>
    </div>
  );
}
